import { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    TextField,
    InputAdornment,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
    ArrowBack as BackIcon,
    Visibility as PreviewIcon,
    Description as WordIcon,
    HealthAndSafety as RiskIcon,
    Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import riskAssessmentToolService, {
    type SavedRiskAssessmentSummary,
} from "../../services/riskAssessmentToolService";
import {
    generateRiskAssessmentDoc,
    type RiskAssessment,
} from "../../services/generateRiskAssessmentDoc";
import { useAuth } from "../../contexts/authContext";
import AssessmentPreview from "./AssessmentPreview";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(3) },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    headerTitle: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
        marginBottom: theme.spacing(2),
    },
    empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
    center: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
}));

const formatWhen = (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const RiskAssessmentLibrary = () => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const { user } = useAuth();
    const myEmail = (user?.details.email || "").toLowerCase();

    const [items, setItems] = useState<SavedRiskAssessmentSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [scope, setScope] = useState<"all" | "mine">("all");
    const [search, setSearch] = useState("");
    const [preview, setPreview] = useState<RiskAssessment | null>(null);
    const [previewTitle, setPreviewTitle] = useState("");
    const [busyId, setBusyId] = useState<number | null>(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        riskAssessmentToolService
            .listLibrary()
            .then((data) => {
                if (active) {
                    setItems(data);
                    setError(null);
                }
            })
            .catch((e) => {
                if (active) setError(typeof e === "string" ? e : "Could not load the library.");
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    const openPreview = async (id: number, title: string | null) => {
        setBusyId(id);
        setError(null);
        try {
            const item = await riskAssessmentToolService.getLibraryItem(id);
            const parsed = item.assessmentJson
                ? (JSON.parse(item.assessmentJson) as RiskAssessment)
                : null;
            setPreview(parsed);
            setPreviewTitle(title || "Risk assessment");
        } catch (e: any) {
            setError(typeof e === "string" ? e : "Could not open that assessment.");
        } finally {
            setBusyId(null);
        }
    };

    const download = async (id: number) => {
        setBusyId(id);
        setError(null);
        try {
            const item = await riskAssessmentToolService.getLibraryItem(id);
            const parsed = item.assessmentJson
                ? (JSON.parse(item.assessmentJson) as RiskAssessment)
                : null;
            if (parsed) await generateRiskAssessmentDoc(parsed);
        } catch (e: any) {
            setError(typeof e === "string" ? e : "Could not download that assessment.");
        } finally {
            setBusyId(null);
        }
    };

    const term = search.trim().toLowerCase();
    const filtered = items.filter((it) => {
        if (scope === "mine") {
            if (!myEmail) return false;
            if ((it.ownerEmail || "").toLowerCase() !== myEmail) return false;
        }
        if (term && !(it.title || "").toLowerCase().includes(term)) return false;
        return true;
    });

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.headerTitle}>
                    <RiskIcon style={{ color: "#7a5cf0", fontSize: 32 }} />
                    <Typography variant="h4">Risk Assessment Library</Typography>
                </Box>
                <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate("/risk-assessment")}>
                    Back to Generator
                </Button>
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} style={{ marginBottom: 16 }}>
                    {error}
                </Alert>
            )}

            <Box className={classes.toolbar}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={scope}
                    onChange={(_e, val) => {
                        if (val) setScope(val);
                    }}
                >
                    <ToggleButton value="all">Everyone</ToggleButton>
                    <ToggleButton value="mine">Mine</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                    size="small"
                    placeholder="Search by title…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ minWidth: 260 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Paper>
                {loading ? (
                    <Box className={classes.center}>
                        <CircularProgress />
                    </Box>
                ) : items.length === 0 ? (
                    <Box className={classes.empty}>
                        <Typography variant="body1">No saved risk assessments yet.</Typography>
                        <Typography variant="body2">
                            Generate one in the Generator, then use Save to Library to keep it here.
                        </Typography>
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box className={classes.empty}>
                        <Typography variant="body1">No assessments match your filters.</Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 600 }}>Title</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Created by</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Last updated</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((it) => (
                                <TableRow key={it.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                            {it.title || "Untitled"}
                                        </Typography>
                                        {it.description ? (
                                            <Typography variant="caption" style={{ color: "#777" }}>
                                                {it.description}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell style={{ color: "#555" }}>{it.ownerEmail || "—"}</TableCell>
                                    <TableCell style={{ color: "#555" }}>
                                        {formatWhen(it.lastModifiedOn || it.createdOn)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Preview">
                                            <span>
                                                <IconButton
                                                    aria-label="Preview"
                                                    onClick={() => openPreview(it.id, it.title)}
                                                    disabled={busyId === it.id}
                                                >
                                                    <PreviewIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Download Word">
                                            <span>
                                                <IconButton
                                                    aria-label="Download Word"
                                                    onClick={() => download(it.id)}
                                                    disabled={busyId === it.id}
                                                >
                                                    <WordIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle>{previewTitle}</DialogTitle>
                <DialogContent dividers>
                    {preview ? <AssessmentPreview assessment={preview} /> : null}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default RiskAssessmentLibrary;
